/**
 * @fileoverview General information fields for the requirement creation form.
 * @module components/RequirementCreationView/RequirementGeneralInfo
 */
import Select from "react-select";
import styles from "./index.module.css";

const CategoryField = ({ categories, form }) => {
  const {
    t,
    requirement,
    selectCategoryRef,
    selectSubcategoryRef,
    setRequirement,
    setSubcategories,
  } = form;

  return (
    <div className={styles.categoryContainer}>
      <label className={styles.label}>
        {t("components.requirementCreationModal.category")}
      </label>
      <Select
        ref={selectCategoryRef}
        options={categories}
        isSearchable={false}
        value={categories.find(
          (option) => option._id === requirement.idCategoria,
        )}
        onChange={(selectedOption) => {
          if (selectedOption) {
            setRequirement((prev) => ({
              ...prev,
              idCategoria: selectedOption._id,
              nombreSubcategoria: null,
            }));
            selectSubcategoryRef.current.clearValue();
            setSubcategories(selectedOption?.subcategorias ?? []);
          }
        }}
        placeholder={t(
          "components.requirementCreationModal.categoryPlaceholder",
        )}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: "8px",
            width: "100%",
            height: "30px",
            fontSize: "16px",
            cursor: "pointer",
            borderColor: "#909092",
          }),
          container: (baseStyles) => ({
            ...baseStyles,
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
          }),
          input: (baseStyles) => ({
            ...baseStyles,
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            cursor: "pointer",
          }),
          indicatorSeparator: () => ({ display: "none" }),
        }}
        getOptionValue={(option) => option._id}
        formatOptionLabel={(option) => <span>{option.nombre}</span>}
      />
    </div>
  );
};

const SubcategoryField = ({ form }) => {
  const { t, requirement, subcategories, selectSubcategoryRef, handleChange } =
    form;

  return (
    <div className={styles.categoryContainer}>
      <label className={styles.label}>
        {t("components.requirementCreationModal.subcategory")}
      </label>
      <Select
        ref={selectSubcategoryRef}
        options={subcategories}
        isDisabled={!requirement.idCategoria}
        isSearchable={false}
        value={subcategories.find(
          (option) => option.value === requirement.nombreSubcategoria,
        )}
        onChange={(selectedOption) => {
          if (selectedOption) {
            handleChange("nombreSubcategoria", selectedOption.nombre);
          }
        }}
        placeholder={t(
          "components.requirementCreationModal.subcategoryPlaceholder",
        )}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: "8px",
            width: "100%",
            height: "30px",
            fontSize: "16px",
            cursor: "pointer",
            // marginBottom: "10px",
            borderColor: "#909092",
          }),
          container: (baseStyles) => ({
            ...baseStyles,
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
          }),
          input: (baseStyles) => ({
            ...baseStyles,
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            cursor: "pointer",
          }),
          indicatorSeparator: () => ({ display: "none" }),
        }}
        getOptionValue={(option) => option.nombre}
        formatOptionLabel={(option) => <span>{option.nombre}</span>}
      />
    </div>
  );
};

const TitleDueDateRow = ({ form }) => {
  const {
    t,
    requirement,
    handleChange,
    handleFieldChange,
    getFieldValidationState,
    getErrorMessage,
  } = form;

  return (
    <div className={styles.generalInfoRow}>
      <div className={styles.titleContainer}>
        <label className={styles.label}>
          {t("components.requirementCreationModal.title")}
        </label>
        <input
          type="text"
          placeholder={t("components.requirementCreationModal.titlePlaceholder")}
          className={`${styles.input} ${
            getFieldValidationState(
              "requerimientoTitulo",
              requirement.titulo || "",
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          value={requirement.titulo || ""}
          onChange={(e) => {
            const value = e.target.value;
            handleChange("titulo", value);
            handleFieldChange("requerimientoTitulo", value);
          }}
          maxLength={100}
        />
        {getErrorMessage("requerimientoTitulo") && (
          <p className={styles.errorMessage}>
            {getErrorMessage("requerimientoTitulo")}
          </p>
        )}
      </div>
      <div className={styles.dueDateContainer}>
        <label className={styles.label}>
          {t("components.requirementCreationModal.dueDate")}
        </label>
        <input
          // inert={true}
          type="date"
          placeholder={t(
            "components.requirementCreationModal.dueDatePlaceholder",
          )}
          className={styles.input}
          style={{
            borderColor: "#909092",
          }}
          value={requirement.fechaLimite || ""}
          // onfocus="(this.type='date')"
          onChange={(e) => handleChange("fechaLimite", e.target.value)}
        />
      </div>
    </div>
  );
};

const DescriptionField = ({ form }) => {
  const {
    t,
    requirement,
    handleChange,
    handleFieldChange,
    getFieldValidationState,
    getErrorMessage,
  } = form;

  return (
    <div className={styles.generalInfoRow}>
      <div className={styles.fullWidthContainer}>
        <label className={styles.label}>
          {t("components.requirementCreationModal.description")}
        </label>
        <textarea
          placeholder={t(
            "components.requirementCreationModal.descriptionPlaceholder",
          )}
          className={`${styles.textarea} ${
            getFieldValidationState(
              "requerimientoDescripcion",
              requirement.descripcion || "",
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          style={{
            borderColor:
              getFieldValidationState(
                "requerimientoDescripcion",
                requirement.descripcion || "",
              ) === "error"
                ? "#dc2626"
                : "#909092",
          }}
          value={requirement.descripcion || ""}
          onChange={(e) => {
            const value = e.target.value;
            handleChange("descripcion", value);
            handleFieldChange("requerimientoDescripcion", value);
          }}
          maxLength={300}
        />
        {getErrorMessage("requerimientoDescripcion") && (
          <p className={styles.errorMessage}>
            {getErrorMessage("requerimientoDescripcion")}
          </p>
        )}
      </div>
    </div>
  );
};

const LocationField = ({ form }) => {
  const {
    t,
    requirement,
    handleChange,
    handleFieldChange,
    getFieldValidationState,
    getErrorMessage,
  } = form;

  return (
    <div className={styles.generalInfoRow}>
      <div className={styles.fullWidthContainer}>
        <label className={styles.label}>
          {t("components.requirementCreationModal.location")}
        </label>
        <input
          type="text"
          placeholder={t(
            "components.requirementCreationModal.locationPlaceholder",
          )}
          className={`${styles.input} ${
            getFieldValidationState(
              "requerimientoUbicacion",
              requirement.ubicacion || "",
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          value={requirement.ubicacion || ""}
          onChange={(e) => {
            const value = e.target.value;
            handleChange("ubicacion", value);
            handleFieldChange("requerimientoUbicacion", value);
          }}
          maxLength={100}
        />
        {getErrorMessage("requerimientoUbicacion") && (
          <p className={styles.errorMessage}>
            {getErrorMessage("requerimientoUbicacion")}
          </p>
        )}
      </div>
    </div>
  );
};

const ExperienceField = ({ form }) => {
  const {
    t,
    requirement,
    handleChange,
    handleFieldChange,
    getFieldValidationState,
    getErrorMessage,
  } = form;

  return (
    <div className={styles.generalInfoRow}>
      <div className={styles.fullWidthContainer}>
        <label className={styles.label}>
          {t("components.requirementCreationModal.minimumExperience")}
        </label>
        <div className={styles.experienceInputContainer}>
          <input
            type="number"
            placeholder={t(
              "components.requirementCreationModal.minimumExperiencePlaceholder",
            )}
            className={`${styles.experienceInput} ${
              getFieldValidationState(
                "requerimientoExperienciaMinima",
                requirement.experienciaMinima || "",
              ) === "error"
                ? styles.inputError
                : ""
            }`}
            value={requirement.experienciaMinima || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^-?\d{0,2}(\.\d{0,1})?$/.test(value)) {
                handleChange("experienciaMinima", value);
                handleFieldChange("requerimientoExperienciaMinima", value);
              }
            }}
          />
          <span className={styles.experienceLabel}>
            {t(
              "components.requirementCreationModal.minimumExperienceInputLabel",
            )}
          </span>
        </div>
        {getErrorMessage("requerimientoExperienciaMinima") && (
          <p className={styles.errorMessage}>
            {getErrorMessage("requerimientoExperienciaMinima")}
          </p>
        )}
      </div>
    </div>
  );
};

const RequirementGeneralInfo = ({ categories, form }) => (
  <div className={styles.generalInfoSection}>
    <div className={styles.generalInfoRow}>
      <CategoryField categories={categories} form={form} />
      <SubcategoryField form={form} />
    </div>
    <TitleDueDateRow form={form} />
    <DescriptionField form={form} />
    <LocationField form={form} />
    <ExperienceField form={form} />
  </div>
);

export default RequirementGeneralInfo;
